import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { formatMoney } from '../utils/formatMoney';
import { DetailPanel, GHOST_BTN, KeyValue, KeyValueItem, Notice, StatusChip } from './shared/listPage';
import { LoadFailed } from './settings/instantSettings';
import { TFn, docTypeLabel, fmtDateTime, orderTypeLabel, paymentLabel, platformLabel, statusChip } from './org/orgFormat';
import { LoadError, loadErrorOf, serverMessage } from './org/orgTypes';
import './org/org.css';

// Ο πίνακας ζει πλέον στο org/orgFormat (μαζί με το docTypeLabel)· η εξαγωγή μένει για όποιον τον εισάγει από εδώ.
export { DOC_TYPE_LABELS } from './org/orgFormat';

/**
 * Λεπτομέρειες παραστατικού ή παραγγελίας για τα κεντρικά (org users) — από 14/10/2026 συρτάρι δεξιά
 * (DetailPanel) αντί για παράθυρο στη μέση. Ο πίνακας «Παραγγελίες & παραστατικά» δείχνει μόνο σύνολα·
 * εδώ ανοίγει η πλήρης εικόνα — στοιχεία πελάτη, γραμμές προϊόντων, ΦΠΑ, MARK. Ίδιες κλήσεις με πριν
 * (GET …/fiscal/:id, …/orders/:id)· τα payloads προς την ΑΑΔΕ μένουν εκτός (τα κόβει το backend).
 * Οι ενέργειες (ακύρωση) έρχονται από τη σελίδα στο `footerStart`, με τους ίδιους κανόνες με πριν.
 */

/** vat_category_id της ΑΑΔΕ → πραγματικός συντελεστής */
const VAT_CATEGORY_RATE: Record<number, number> = { 1: 24, 2: 13, 3: 6, 4: 17, 5: 9, 6: 4, 7: 0 };

/** payment_method_id της ΑΑΔΕ → κλειδί κειμένου */
const PAYMENT_METHOD_KEYS: Record<number, string> = {
  1: 'domesticAccount', 2: 'foreignAccount', 3: 'cash', 4: 'cheque', 5: 'credit', 6: 'webBanking', 7: 'pos', 8: 'iris',
};

const money = (v: unknown) => formatMoney(Number(v) || 0);

interface Props {
  orgId: number;
  storeId: number;
  kind: 'fiscal' | 'order';
  recordId: number;
  onClose: () => void;
  /** Άνοιγμα άλλου παραστατικού από τη λίστα της παραγγελίας */
  onOpenFiscal?: (documentId: number) => void;
  /** Αριστερά στο κάτω μέρος: η ενέργεια της σελίδας (π.χ. «Ακύρωση παραγγελίας»). */
  footerStart?: React.ReactNode;
}

const OrgRecordDetailsModal: React.FC<Props> = ({ orgId, storeId, kind, recordId, onClose, onOpenFiscal, footerStart }) => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'common', 'listPage']);
  const t = tRaw as unknown as TFn;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ kind: LoadError; message: string } | null>(null);
  const [data, setData] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = kind === 'fiscal'
        ? await organizationAPI.storeFiscalDetail(orgId, storeId, recordId)
        : await organizationAPI.storeOrderDetail(orgId, storeId, recordId);
      setData(res);
    } catch (e) {
      setError({ kind: loadErrorOf(e), message: serverMessage(e, t('orgDashboard:record.loadFailed')) });
    } finally {
      setLoading(false);
    }
  }, [orgId, storeId, kind, recordId, t]);

  useEffect(() => { load(); }, [load]);

  const doc = kind === 'fiscal' ? data?.document : null;
  const order = kind === 'order' ? data?.order : null;
  const docLabel = (code: string | null | undefined) => (code ? docTypeLabel(t, code) : t('orgDashboard:record.document'));

  const title = kind === 'fiscal'
    ? (doc ? `${docLabel(doc.invoice_type_code)}${doc.series ? ` ${doc.series}/${doc.document_number}` : ''}` : t('orgDashboard:record.document'))
    : t('orgDashboard:record.orderTitle', { code: order?.short_code || recordId });
  const subtitle = kind === 'fiscal'
    ? (doc ? [doc.invoice_type_code, fmtDateTime(doc.issued_at || doc.created_at, true)].filter(Boolean).join(' · ') : undefined)
    : (order ? fmtDateTime(order.order_timestamp, true) : undefined);

  const chip = (status: string | null | undefined) => {
    const c = statusChip(t, status);
    return <StatusChip tone={c.tone}>{c.label}</StatusChip>;
  };

  const section = (id: string, heading: React.ReactNode, children: React.ReactNode) => (
    <section aria-labelledby={`org-rec-${id}`} className="org-rec-sec">
      <h3 id={`org-rec-${id}`} className="org-panel-h">{heading}</h3>
      {children}
    </section>
  );

  let body: React.ReactNode = null;
  if (loading && !data) {
    body = <p className="text-sm text-t3" role="status">{t('common:loading')}</p>;
  } else if (error) {
    body = (
      <LoadFailed
        message={error.kind === 'forbidden' ? t('orgDashboard:record.forbidden') : error.message}
        onRetry={load}
      />
    );
  } else if (doc) {
    const docItems: KeyValueItem[] = [
      { id: 'type', label: t('orgDashboard:record.type'), value: docLabel(doc.invoice_type_code) },
      { id: 'number', label: t('orgDashboard:record.seriesNumber'), value: doc.series ? `${doc.series}/${doc.document_number}` : (doc.document_number ?? null) },
      { id: 'date', label: t('common:date'), value: fmtDateTime(doc.issued_at || doc.created_at, true) },
      { id: 'status', label: t('common:status'), value: chip(doc.status) },
      {
        id: 'payment',
        label: t('orgDashboard:record.paymentMethod'),
        value: PAYMENT_METHOD_KEYS[doc.payment_method_id] ? t(`orgDashboard:record.aadePayment.${PAYMENT_METHOD_KEYS[doc.payment_method_id]}`) : (doc.payment_method_id ?? null),
      },
      { id: 'order', label: t('orgDashboard:record.order'), value: doc.short_code || doc.order_id || null },
      ...(doc.mark ? [{ id: 'mark', label: 'MARK', value: <span className="font-mono text-xs">{doc.mark}</span> }] : []),
      ...(doc.oxygen_uid ? [{ id: 'uid', label: 'UID', value: <span className="font-mono text-xs">{doc.oxygen_uid}</span> }] : []),
    ];
    const lines: any[] = data.lines || [];
    body = (
      <div className="space-y-7">
        {(doc.error_message || doc.failure_message) && (
          <Notice tone="danger" testId="org-rec-error">{doc.error_message || doc.failure_message}</Notice>
        )}
        {section('doc', t('orgDashboard:record.docSection'), <KeyValue items={docItems} testId="org-rec-doc" />)}
        {(doc.customer_name || doc.customer_vat_number || doc.customer_address) && section('customer', t('orgDashboard:record.customerSection'), (
          <KeyValue
            items={[
              { id: 'name', label: t('orgDashboard:record.companyName'), value: doc.customer_name || null },
              { id: 'vat', label: t('orgDashboard:record.vatNumber'), value: doc.customer_vat_number || null },
              { id: 'phone', label: t('common:phone'), value: doc.customer_phone || null },
              { id: 'address', label: t('orgDashboard:record.address'), value: doc.customer_address || doc.order_address || null },
              { id: 'city', label: t('orgDashboard:record.city'), value: doc.customer_city || null },
              { id: 'zip', label: t('orgDashboard:record.postalCode'), value: doc.customer_postal_code || null },
            ]}
          />
        ))}
        {section('lines', t('orgDashboard:record.linesSection', { count: lines.length }), (
          <>
            {lines.length === 0 ? (
              <p className="text-sm text-t3">{t('orgDashboard:record.noLines')}</p>
            ) : (
              <div className="org-lines-wrap">
                <table className="org-lines" data-testid="org-rec-lines">
                  <thead>
                    <tr>
                      <th>{t('orgDashboard:record.colDescription')}</th>
                      <th>{t('orgDashboard:record.colQty')}</th>
                      <th>{t('orgDashboard:record.colPrice')}</th>
                      <th className="org-opt">{t('orgDashboard:record.colNet')}</th>
                      <th>{t('orgDashboard:record.colVat')}</th>
                      <th>{t('orgDashboard:record.colTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i}>
                        <td>
                          {l.description || l.item_code || '—'}
                          {l.line_comments && <span className="org-lines-sub">{l.line_comments}</span>}
                        </td>
                        <td>{Number(l.quantity) || 0}</td>
                        <td>{money(l.unit_price)}</td>
                        <td className="org-opt">{money(l.net_amount)}</td>
                        <td>
                          {money(l.vat_amount)}
                          {VAT_CATEGORY_RATE[l.vat_category_id] != null && <span className="org-lines-sub">{VAT_CATEGORY_RATE[l.vat_category_id]}%</span>}
                        </td>
                        <td className="strong">{money(l.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <dl className="org-totals" data-testid="org-rec-totals">
              <div><dt>{t('orgDashboard:record.netTotal')}</dt><dd>{money(doc.total_net_amount)}</dd></div>
              <div><dt>{t('orgDashboard:record.vatTotal')}</dt><dd>{money(doc.total_vat_amount)}</dd></div>
              <div className="is-total"><dt>{t('orgDashboard:record.grandTotal')}</dt><dd>{money(doc.total_gross_amount)}</dd></div>
            </dl>
          </>
        ))}
      </div>
    );
  } else if (order) {
    const items: any[] = data.items || [];
    const docs: any[] = data.documents || [];
    body = (
      <div className="space-y-7">
        {order.cancel_reason && (
          <Notice testId="org-rec-cancel-reason">{t('orgDashboard:record.cancelledReason', { reason: order.cancel_reason })}</Notice>
        )}
        {section('order', t('orgDashboard:record.orderSection'), (
          <KeyValue
            testId="org-rec-order"
            items={[
              { id: 'code', label: t('orgDashboard:record.code'), value: order.short_code || order.id },
              { id: 'date', label: t('common:date'), value: fmtDateTime(order.order_timestamp, true) },
              { id: 'type', label: t('orgDashboard:record.type'), value: order.order_type ? orderTypeLabel(t, order.order_type) : null },
              { id: 'channel', label: t('orgDashboard:record.channel'), value: order.platform_name ? platformLabel(t, order.platform_name) : null },
              { id: 'payment', label: t('orgDashboard:record.payment'), value: order.payment_type ? paymentLabel(t, order.payment_type) : null },
              { id: 'status', label: t('common:status'), value: chip(order.status) },
            ]}
          />
        ))}
        {(order.customer_name || order.customer_phone || order.customer_address) && section('customer', t('orgDashboard:record.customerSection'), (
          <KeyValue
            items={[
              { id: 'name', label: t('orgDashboard:record.name'), value: order.customer_name || null },
              { id: 'phone', label: t('common:phone'), value: order.customer_phone || null },
              { id: 'address', label: t('orgDashboard:record.address'), value: order.customer_address || null },
            ]}
          />
        ))}
        {section('items', t('orgDashboard:record.itemsSection', { count: items.length }), (
          <>
            {items.length === 0 ? (
              <p className="text-sm text-t3">{t('orgDashboard:record.noItems')}</p>
            ) : (
              <div className="org-lines-wrap">
                <table className="org-lines" data-testid="org-rec-items">
                  <thead>
                    <tr>
                      <th>{t('orgDashboard:record.colProduct')}</th>
                      <th>{t('orgDashboard:record.colQty')}</th>
                      <th>{t('orgDashboard:record.colPrice')}</th>
                      <th>{t('orgDashboard:record.colTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i}>
                        <td>
                          {it.product_name}
                          {it.product_notes && <span className="org-lines-sub org-lines-note">{it.product_notes}</span>}
                          {it.materials && <span className="org-lines-sub">{it.materials}</span>}
                        </td>
                        <td>{it.quantity}</td>
                        <td>{money(it.unit_price)}</td>
                        <td className="strong">{money(it.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <dl className="org-totals" data-testid="org-rec-totals">
              {Number(order.delivery_fee) > 0 && (
                <div><dt>{t('orgDashboard:record.deliveryFee')}</dt><dd>{money(order.delivery_fee)}</dd></div>
              )}
              <div className="is-total"><dt>{t('orgDashboard:record.grandTotal')}</dt><dd>{money(order.total_amount)}</dd></div>
            </dl>
          </>
        ))}
        {docs.length > 0 && section('docs', t('orgDashboard:record.docsSection'), (
          <ul className="org-doc-links">
            {docs.map(d => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => onOpenFiscal?.(d.id)}
                  disabled={!onOpenFiscal}
                  className="org-doc-link"
                  data-testid={`org-rec-doc-${d.id}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-t1 truncate">
                      {docLabel(d.invoice_type_code)}{d.series ? ` · ${d.series}/${d.document_number}` : ''}
                    </span>
                    <span className="block text-xs text-t3">{fmtDateTime(d.created_at, true)} · {statusChip(t, d.status).label}</span>
                  </span>
                  <span className="text-sm font-semibold text-t1 shrink-0 tabular-nums">{money(d.total_gross_amount)}</span>
                </button>
              </li>
            ))}
          </ul>
        ))}
      </div>
    );
  }

  return (
    <DetailPanel
      open
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="lg"
      testId="org-record"
      footerStart={footerStart}
      footer={<button type="button" className={GHOST_BTN} onClick={onClose}>{t('listPage:close')}</button>}
    >
      {body}
    </DetailPanel>
  );
};

export default OrgRecordDetailsModal;
